import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';
import CustomerFacingSubtotal from '../../components/checkout/CustomerFacingSubtotal';
import { getOrderTaxName } from '../../utils/merchantTax';

const currency = new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' });

export default function POSPayment() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [paymentCredentials, setPaymentCredentials] = useState({});

  // Get store slug from subdomain
  const getStoreSlugFromSubdomain = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('.')) {
      const parts = hostname.split('.');
      if (parts.length >= 2 && parts[0] !== 'www' && parts[0] !== 'localhost') {
        return parts[0];
      }
    }
    return '';
  };

  const storeSlug = getStoreSlugFromSubdomain();

  // Fetch order details
  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/checkout/pos-order/${orderId}`);

      if (response.data.success) {
        setOrder(response.data.order);
        setStore(response.data.store);

        // Check if already paid
        if (response.data.order.paymentStatus === 'PAID') {
          setSuccess(true);
        }
      } else {
        setError(response.data.error || 'Order not found');
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.response?.data?.error || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Fetch payment credentials using store slug
  const fetchPaymentCredentials = useCallback(async (slug) => {
    if (!slug) return;

    try {
      const response = await axios.get(`/merchant/payment-gateways/store/${slug}/credentials`);

      if (response.data.status === 'success') {
        setPaymentCredentials(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching payment credentials:', err);
    }
  }, []);

  // Fetch order on mount
  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // Fetch payment credentials when store is loaded
  useEffect(() => {
    const slug = store?.slug || storeSlug;
    if (slug) {
      fetchPaymentCredentials(slug);
    }
  }, [store, storeSlug, fetchPaymentCredentials]);

  // Handle payment verification
  const handlePaymentVerification = async (reference, provider = 'paystack') => {
    try {
      const response = await axios.post(`/checkout/pos-order/${orderId}/verify-payment`, { reference, provider });

      if (response.data.success) {
        setSuccess(true);
        setOrder(response.data.order);
      } else {
        setError('Payment verification failed. Please contact support.');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setError('Failed to verify payment. Please contact support with reference: ' + reference);
    } finally {
      setPaying(false);
    }
  };

  // Handle Paystack payment
  const handlePaystackPayment = () => {
    if (!window.PaystackPop) {
      setError('Payment system not loaded. Please refresh the page.');
      return;
    }

    const paystackKey = paymentCredentials.paystack?.publicKey;
    if (!paystackKey) {
      setError('Payment not configured for this store. Please contact the merchant.');
      return;
    }

    // Paystack requires a valid email - generate one if not provided
    const customerEmail = order.customerInfo?.email || `pos-${order.orderNumber}@customer.userevstar.com`;

    setPaying(true);
    setError(null);
    const transactionRef = `pos_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      const handler = window.PaystackPop.setup({
        key: paystackKey,
        email: customerEmail,
        amount: Math.round(order.total * 100), // Paystack expects amount in kobo
        currency: 'NGN',
        ref: transactionRef,
        metadata: {
          order_id: orderId,
          order_number: order.orderNumber,
          source: 'POS',
          customer_name: order.customerInfo?.name || 'Walk-in Customer',
          customer_phone: order.customerInfo?.phone || ''
        },
        callback: (response) => {
          console.log('Paystack payment successful:', response);
          handlePaymentVerification(response.reference, 'paystack');
        },
        onClose: () => {
          setPaying(false);
        }
      });

      if (handler && typeof handler.openIframe === 'function') {
        handler.openIframe();
      } else if (handler && typeof handler.open === 'function') {
        handler.open();
      } else {
        throw new Error('Payment initialization failed');
      }
    } catch (err) {
      console.error('Paystack error:', err);
      setPaying(false);
      setError('Failed to initialize payment. Please try again.');
    }
  };

  // Handle Flutterwave payment
  const handleFlutterwavePayment = () => {
    if (!window.FlutterwaveCheckout) {
      setError('Payment system not loaded. Please refresh the page.');
      return;
    }

    const flutterwaveKey = paymentCredentials.flutterwave?.publicKey;
    if (!flutterwaveKey) {
      setError('Flutterwave not configured. Please use card payment.');
      return;
    }

    setPaying(true);
    const transactionRef = `pos_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    window.FlutterwaveCheckout({
      public_key: flutterwaveKey,
      tx_ref: transactionRef,
      amount: order.total,
      currency: 'NGN',
      payment_options: 'card,mobilemoney,ussd',
      customer: {
        email: order.customerInfo?.email || `pos-${order.orderNumber}@customer.userevstar.com`,
        name: order.customerInfo?.name || 'Walk-in Customer',
        phone_number: order.customerInfo?.phone || ''
      },
      customizations: {
        title: store?.name || 'Store Payment',
        description: `Payment for Order ${order.orderNumber}`,
        logo: store?.logo || ''
      },
      callback: (response) => {
        console.log('Flutterwave payment response:', response);
        if (response.status === 'successful' || response.status === 'completed') {
          const verificationReference = response.transaction_id || response.id || response.tx_ref;
          if (!verificationReference) {
            setPaying(false);
            setError('Payment completed, but no verification reference was returned. Please contact support.');
            return;
          }
          handlePaymentVerification(verificationReference, 'flutterwave');
        } else {
          setPaying(false);
          setError('Payment was not successful. Please try again.');
        }
      },
      onclose: () => {
        setPaying(false);
      }
    });
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loading}>Loading order details...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !order) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>!</div>
          <h2 style={styles.errorTitle}>Unable to Load Order</h2>
          <p style={styles.errorMessage}>{error}</p>
          <div style={styles.buttonRow}>
            <button style={styles.retryButton} onClick={fetchOrder}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.successTitle}>Payment Successful!</h2>
          <p style={styles.successMessage}>
            Your payment for order <strong>{order?.orderNumber}</strong> has been confirmed.
          </p>
          <div style={styles.orderSummary}>
            <div style={styles.summaryRow}>
              <span>Amount Paid</span>
              <span style={styles.amount}>{currency.format(order?.total || 0)}</span>
            </div>
          </div>
          <p style={styles.thankYou}>Thank you for your purchase!</p>
        </div>
      </div>
    );
  }

  // Payment form
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Store Header */}
        <div style={styles.header}>
          {store?.logo && (
            <img src={store.logo} alt={store.name} style={styles.logo} />
          )}
          <h1 style={styles.storeName}>{store?.name || 'Store'}</h1>
        </div>

        {/* Order Info */}
        <div style={styles.orderInfo}>
          <h2 style={styles.title}>Complete Your Payment</h2>
          <p style={styles.orderNumber}>Order: {order?.orderNumber}</p>
        </div>

        {/* Order Items */}
        <div style={styles.itemsSection}>
          <h3 style={styles.sectionTitle}>Order Summary</h3>
          {order?.items?.map((item, index) => {
            // Order items come from multiple channels with slightly
            // different shapes: POS items carry `total` precomputed
            // and use `quantity`; storefront / manual / WhatsApp items
            // carry just `price` per unit, may use `qty`, and have no
            // line total. Compute defensively so we never render NaN.
            const qty = Number(item.quantity ?? item.qty ?? 1) || 1;
            const lineTotal =
              item.total != null
                ? Number(item.total)
                : (parseFloat(item.price) || 0) * qty;
            return (
              <div key={index} style={styles.itemRow}>
                <div style={styles.itemInfo}>
                  <span style={styles.itemName}>{item.name}</span>
                  {item.variantName && (
                    <span style={styles.variantName}> - {item.variantName}</span>
                  )}
                  <span style={styles.itemQty}> × {qty}</span>
                </div>
                <span style={styles.itemPrice}>{currency.format(lineTotal)}</span>
              </div>
            );
          })}
        </div>

        {/* Totals */}
        <div style={styles.totalsSection}>
          <CustomerFacingSubtotal
            subtotal={order?.subtotal || 0}
            processingFee={
              order?.serviceCharge > 0
                && String(order?.serviceChargeWhoPays || 'customer').toLowerCase() === 'customer'
                ? order.serviceCharge
                : 0
            }
            formatCurrency={(amount) => currency.format(amount || 0)}
            compact
            rowStyle={styles.totalRow}
            labelStyle={{ color: '#6b7280', fontWeight: 400 }}
            valueStyle={{ color: '#6b7280', fontWeight: 400 }}
            subLabel={false}
          />
          {order?.discountTotal > 0 && (
            <div style={styles.totalRow}>
              <span>Discount</span>
              <span style={styles.discount}>-{currency.format(order.discountTotal)}</span>
            </div>
          )}
          {order?.taxTotal > 0 && (
            <div style={styles.totalRow}>
              <span>{getOrderTaxName(order)}</span>
              <span>{currency.format(order.taxTotal)}</span>
            </div>
          )}
          {(order?.shippingFee > 0 || order?.deliveryFee > 0) && (
            <div style={styles.totalRow}>
              <span>Delivery</span>
              <span>{currency.format(order.shippingFee || order.deliveryFee)}</span>
            </div>
          )}
          <div style={styles.grandTotal}>
            <span>Total</span>
            <span>{currency.format(order?.total || 0)}</span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={styles.errorBanner}>{error}</div>
        )}

        {/* Payment Buttons - Only show enabled gateways */}
        {paymentCredentials.paystack?.publicKey && (
          <div style={styles.buttonRow}>
            <button
              style={paying ? styles.payButtonDisabled : styles.payButton}
              onClick={handlePaystackPayment}
              disabled={paying}
            >
              {paying ? 'Processing...' : `Pay with Card ${currency.format(order?.total || 0)}`}
            </button>
          </div>
        )}

        {paymentCredentials.flutterwave?.publicKey && (
          <div style={styles.buttonRow}>
            <button
              style={paymentCredentials.paystack?.publicKey ? styles.altPayButton : (paying ? styles.payButtonDisabled : styles.payButton)}
              onClick={handleFlutterwavePayment}
              disabled={paying}
            >
              {paying ? 'Processing...' : `Pay with Flutterwave ${currency.format(order?.total || 0)}`}
            </button>
          </div>
        )}

        {/* No payment methods available */}
        {!paymentCredentials.paystack?.publicKey && !paymentCredentials.flutterwave?.publicKey && (
          <div style={styles.errorBanner}>
            No payment methods available. Please contact the merchant.
          </div>
        )}

        {/* Security Note */}
        {(paymentCredentials.paystack?.publicKey || paymentCredentials.flutterwave?.publicKey) && (
          <p style={styles.securityNote}>
            Your payment information is secure and encrypted.
          </p>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '32px',
    width: '100%',
    maxWidth: '480px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px'
  },
  logo: {
    width: '64px',
    height: '64px',
    borderRadius: '12px',
    objectFit: 'cover',
    marginBottom: '12px'
  },
  storeName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1a1a2e',
    margin: 0
  },
  orderInfo: {
    textAlign: 'center',
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 8px 0'
  },
  orderNumber: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  itemsSection: {
    borderTop: '1px solid #e5e7eb',
    paddingTop: '16px',
    marginBottom: '16px'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#6b7280',
    margin: '0 0 12px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #f3f4f6'
  },
  itemInfo: {
    flex: 1
  },
  itemName: {
    fontSize: '14px',
    color: '#1a1a2e',
    fontWeight: '500'
  },
  variantName: {
    fontSize: '12px',
    color: '#6b7280'
  },
  itemQty: {
    fontSize: '12px',
    color: '#6b7280'
  },
  itemPrice: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  totalsSection: {
    borderTop: '1px solid #e5e7eb',
    paddingTop: '16px',
    marginBottom: '24px'
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    fontSize: '14px',
    color: '#6b7280'
  },
  discount: {
    color: '#10b981'
  },
  grandTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a1a2e',
    borderTop: '2px solid #1a1a2e',
    marginTop: '8px'
  },
  payButton: {
    width: '100%',
    padding: '14px 28px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#fff',
    background: '#0d7463',
    border: '1px solid #0d7463',
    borderRadius: '10px',
    cursor: 'pointer',
    minWidth: '260px',
    transition: 'background 0.15s, border-color 0.15s'
  },
  payButtonDisabled: {
    padding: '14px 28px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#fff',
    background: '#9ca3af',
    border: '1px solid #9ca3af',
    borderRadius: '10px',
    cursor: 'not-allowed',
    minWidth: '260px'
  },
  altPayButton: {
    padding: '13px 28px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#0d7463',
    background: '#fff',
    border: '1px solid #c7e8e0',
    borderRadius: '10px',
    cursor: 'pointer',
    minWidth: '260px'
  },
  securityNote: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#9ca3af',
    marginTop: '16px'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#6b7280',
    fontSize: '16px'
  },
  errorIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: '#fee2e2',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: 'bold',
    margin: '0 auto 16px'
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1a1a2e',
    textAlign: 'center',
    margin: '0 0 8px 0'
  },
  errorMessage: {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
    margin: '0 0 24px 0'
  },
  errorBanner: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center'
  },
  retryButton: {
    padding: '12px 28px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#fff',
    background: '#0d7463',
    border: '1px solid #0d7463',
    borderRadius: '10px',
    cursor: 'pointer',
    minWidth: '180px'
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '12px'
  },
  successIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: '#d1fae5',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '40px',
    fontWeight: 'bold',
    margin: '0 auto 16px'
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1a1a2e',
    textAlign: 'center',
    margin: '0 0 8px 0'
  },
  successMessage: {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
    margin: '0 0 24px 0'
  },
  orderSummary: {
    background: '#f9fafb',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px'
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: '#6b7280'
  },
  amount: {
    fontWeight: '700',
    color: '#10b981',
    fontSize: '18px'
  },
  thankYou: {
    textAlign: 'center',
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  }
};
